import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { throttle } from 'lodash';
import * as colorPicker from '@zag-js/color-picker';
import { ColorPicker } from '@/UI/ColorPicker/ColorPicker';
import { I18N_KEYS } from '@/language/keys';
import ParentMenuItem from '../ParentMenuItem';

interface ColorMenuItemProps {
  currentColor: string;
  onColorChange: (color: string) => void;
}

export const ColorMenuItem = ({ currentColor, onColorChange }: ColorMenuItemProps) => {
  const { t } = useTranslation();

  // Create throttled color update function that persists across renders
  const throttledUpdateColor = useRef(
    throttle((color: string) => {
      onColorChange(color);
    }, 100), // Update at most every 100ms
  ).current;

  // Cleanup: cancel any pending throttled calls on unmount
  useEffect(() => {
    return () => {
      throttledUpdateColor.cancel();
    };
  }, [throttledUpdateColor]);

  const handleColorChange = useCallback(
    (color: colorPicker.Color) => {
      throttledUpdateColor(color.toString('hex'));
    },
    [throttledUpdateColor],
  );

  const [eyedropperActive, setEyedropperActive] = useState(false);

  return (
    <ParentMenuItem label={t(I18N_KEYS.GENERAL.COLOR)} offset={{ left: '10px' }} sticky={eyedropperActive}>
      <ColorPicker
        color={colorPicker.parse(currentColor)}
        onChange={handleColorChange}
        showAlpha={false}
        onEyedropperActiveChange={setEyedropperActive}
      />
    </ParentMenuItem>
  );
};
